import { useState, type FormEvent } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/BrandingContext";
import { PendingApprovalView } from "./components/PendingApprovalView";
import { DeviceUnauthorizedModal } from "./components/DeviceUnauthorizedModal";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface LoginErrors {
  email?: string;
  password?: string;
}

interface PendingDetails {
  ipAddress?: string;
  requestedAt?: string;
  browser?: string;
  os?: string;
}

interface UnauthorizedData {
  ipAddress?: string;
  status: "REJECTED" | "REVOKED";
  rejectReason?: string | null;
}

export default function LoginPage() {
  const { login } = useAuth();
  const { businessName, tagline, effectiveLogo } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<LoginErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Device / IP verification states
  const [isPending, setIsPending] = useState(false);
  const [pendingDetails, setPendingDetails] = useState<PendingDetails | null>(null);

  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false);
  const [unauthorizedData, setUnauthorizedData] = useState<UnauthorizedData | null>(null);

  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const validate = (): boolean => {
    const next: LoginErrors = {};
    if (!email) next.email = "Email is required.";
    else if (!EMAIL_RE.test(email)) next.email = "Enter a valid email address.";
    if (!password) next.password = "Password is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const doLogin = async () => {
    if (submitting) return;
    setFormError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const errObj = err as {
        code?: string;
        message?: string;
        details?: {
          error?: {
            code?: string;
            message?: string;
            details?: {
              ipAddress?: string;
              requestedAt?: string;
              browser?: string;
              os?: string;
              status?: "REJECTED" | "REVOKED";
              rejectReason?: string | null;
            };
          };
        };
      };

      const nestedError = errObj?.details?.error;
      const code = nestedError?.code || errObj?.code;
      const details = nestedError?.details;

      if (code === "DEVICE_PENDING_APPROVAL") {
        setPendingDetails({
          ipAddress: details?.ipAddress || "",
          requestedAt: details?.requestedAt,
          browser: details?.browser,
          os: details?.os,
        });
        setIsPending(true);
        setSubmitting(false);
        return;
      }

      if (code === "DEVICE_UNAUTHORIZED") {
        setUnauthorizedData({
          ipAddress: details?.ipAddress || "",
          status: details?.status || "REJECTED",
          rejectReason: details?.rejectReason || null,
        });
        setShowUnauthorizedModal(true);
        setSubmitting(false);
        return;
      }

      const message = err instanceof Error ? err.message : "Unable to sign in. Please try again.";
      setFormError(message);
      setSubmitting(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    doLogin();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <span className="flex h-36 w-36 items-center justify-center rounded-2xl bg-white border border-background-200 p-0 shadow-sm overflow-hidden">
            <img src={effectiveLogo} alt={businessName} className="h-full w-full object-contain" />
          </span>
          {/* <h1 className="mt-4 text-xl font-semibold text-foreground-950 text-center">{businessName}</h1>
          <p className="mt-1 text-sm text-foreground-500 text-center">{tagline}</p> */}
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-6 shadow-sm">
          {isPending && pendingDetails ? (
            <PendingApprovalView
              email={email}
              password={password}
              details={pendingDetails}
              onBackToLogin={() => setIsPending(false)}
              onApproved={doLogin}
            />
          ) : (
            <>
              <h2 className="text-base font-semibold text-foreground-900">Sign in</h2>
              <p className="mt-0.5 text-sm text-foreground-500">Enter your credentials to access the workspace.</p>

              {formError ? (
                <div className="mt-4 flex items-start gap-2 rounded-md border border-danger-200 bg-danger-50 px-3 py-2.5 text-sm text-danger-700">
                  <i className="ri-close-circle-line mt-0.5 flex-none text-base" />
                  <span>{formError}</span>
                </div>
              ) : null}

              <form onSubmit={handleSubmit} className="mt-5 space-y-4" noValidate>
                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-xs font-medium text-foreground-700">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@mkfinancial.in"
                    className={`h-9 w-full rounded-md border bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40 ${
                      errors.email ? "border-danger-300" : "border-background-300"
                    }`}
                  />
                  {errors.email ? <p className="text-xs text-danger-600">{errors.email}</p> : null}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="password" className="block text-xs font-medium text-foreground-700">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={`h-9 w-full rounded-md border bg-background-50 px-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40 ${
                        errors.password ? "border-danger-300" : "border-background-300"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute inset-y-0 right-0 flex w-10 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
                    >
                      <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-base`} />
                    </button>
                  </div>
                  {errors.password ? <p className="text-xs text-danger-600">{errors.password}</p> : null}
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground-700">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      className="h-4 w-4 cursor-pointer accent-primary-600"
                    />
                    Remember me
                  </label>
                  <Link to="/forgot-password" className="text-sm font-medium text-primary-600 hover:underline">
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 text-sm font-medium text-white transition-colors hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400/60 disabled:cursor-not-allowed disabled:bg-primary-300"
                >
                  {submitting ? (
                    <i className="ri-loader-4-line animate-spin text-base" />
                  ) : (
                    <i className="ri-login-box-line text-base" />
                  )}
                  {submitting ? "Signing in…" : "Sign in"}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="mt-5 text-center text-xs text-foreground-500">
          Need an account? Ask an admin.
        </p>
      </div>

      {showUnauthorizedModal && unauthorizedData ? (
        <DeviceUnauthorizedModal
          open={showUnauthorizedModal}
          onClose={() => setShowUnauthorizedModal(false)}
          email={email}
          password={password}
          ipAddress={unauthorizedData.ipAddress}
          status={unauthorizedData.status}
          rejectReason={unauthorizedData.rejectReason}
          onRequestSubmitted={(details) => {
            setPendingDetails(details);
            setIsPending(true);
          }}
        />
      ) : null}
    </div>
  );
}